/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { typographyRamp, monoTypographyRamp } from '@uber/base-specs/src/generated/typography/v2/typography';
import { typographyRampCssVars, monoTypographyRampCssVars } from '@uber/base-specs/src/generated/typography/v2/typography-css-vars';
import { mapV1TypographyToV2, mapV1MonoTypographyToV2 } from '@uber/base-specs/src/utils/mapV1ToV2Typography';
import type { TypographyRampKey, MonoTypographyRampKey } from '@uber/base-specs/src/generated/typography/v2/typography';
import type { V1TypographyKey, V1MonoTypographyKey } from '@uber/base-specs/src/utils/mapV1ToV2Typography';
import type { Font, Typography } from '../../../themes';

/**
 * `theme.typography`'s named styles predate `@uber/base-specs`'s Typography 2.0
 * naming convention (e.g. `HeadingXSmall` vs. `headingXsmall` — note the "X"
 * capitalization differs for the `X*`/`XX*` size tiers), so a mechanical
 * lowercase-first-letter transform doesn't reliably produce a valid
 * `V1TypographyKey`. This explicit table is the translation boundary between
 * the two naming conventions; `mapV1TypographyToV2` (from `@uber/base-specs`)
 * does the actual v1 → v2 tier remapping once translated.
 *
 * Only named styles are listed here — the raw `font100`…`font1450` scale
 * entries are migrated separately via {@link RAW_SCALE_TO_BASEUI_KEY}, which
 * routes each through the named style it backs.
 */
const BASEUI_TO_V1_TYPOGRAPHY_KEY: Record<string, V1TypographyKey> = {
  ParagraphXSmall: 'paragraphXsmall',
  ParagraphSmall: 'paragraphSmall',
  ParagraphMedium: 'paragraphMedium',
  ParagraphLarge: 'paragraphLarge',
  LabelXSmall: 'labelXsmall',
  LabelSmall: 'labelSmall',
  LabelMedium: 'labelMedium',
  LabelLarge: 'labelLarge',
  HeadingXSmall: 'headingXsmall',
  HeadingSmall: 'headingSmall',
  HeadingMedium: 'headingMedium',
  HeadingLarge: 'headingLarge',
  HeadingXLarge: 'headingXlarge',
  HeadingXXLarge: 'headingXxlarge',
  DisplayXSmall: 'displayXsmall',
  DisplaySmall: 'displaySmall',
  DisplayMedium: 'displayMedium',
  DisplayLarge: 'displayLarge'
};

/** Mono counterpart of {@link BASEUI_TO_V1_TYPOGRAPHY_KEY}. */
const BASEUI_TO_V1_MONO_TYPOGRAPHY_KEY: Record<string, V1MonoTypographyKey> = {
  MonoParagraphXSmall: 'monoParagraphXsmall',
  MonoParagraphSmall: 'monoParagraphSmall',
  MonoParagraphMedium: 'monoParagraphMedium',
  MonoParagraphLarge: 'monoParagraphLarge',
  MonoLabelXSmall: 'monoLabelXsmall',
  MonoLabelSmall: 'monoLabelSmall',
  MonoLabelMedium: 'monoLabelMedium',
  MonoLabelLarge: 'monoLabelLarge',
  MonoHeadingXSmall: 'monoHeadingXsmall',
  MonoHeadingSmall: 'monoHeadingSmall',
  MonoHeadingMedium: 'monoHeadingMedium',
  MonoHeadingLarge: 'monoHeadingLarge',
  MonoHeadingXLarge: 'monoHeadingXlarge',
  MonoHeadingXXLarge: 'monoHeadingXxlarge',
  MonoDisplayXSmall: 'monoDisplayXsmall',
  MonoDisplaySmall: 'monoDisplaySmall',
  MonoDisplayMedium: 'monoDisplayMedium',
  MonoDisplayLarge: 'monoDisplayLarge'
};

/**
 * Maps each raw `font*` scale entry to the named BaseUI style it backs (see
 * `themes/shared/typography.ts`, e.g. `HeadingLarge: font850`). Many
 * consumers reach for the raw scale directly (`theme.typography.font850`)
 * rather than the named alias, so the migration follows through to these
 * too — each raw key migrates to the *same* v2 style as the named style it
 * backs, resolved via {@link BASEUI_TO_V1_TYPOGRAPHY_KEY}. There's no mono
 * raw-scale analog — v1 only defines named `Mono*` keys, no `monoFont*`
 * scale entries.
 */
const RAW_SCALE_TO_BASEUI_KEY: Record<string, string> = {
  font100: 'ParagraphXSmall',
  font150: 'LabelXSmall',
  font200: 'ParagraphSmall',
  font250: 'LabelSmall',
  font300: 'ParagraphMedium',
  font350: 'LabelMedium',
  font400: 'ParagraphLarge',
  font450: 'LabelLarge',
  font550: 'HeadingXSmall',
  font650: 'HeadingSmall',
  font750: 'HeadingMedium',
  font850: 'HeadingLarge',
  font950: 'HeadingXLarge',
  font1050: 'HeadingXXLarge',
  font1150: 'DisplayXSmall',
  font1250: 'DisplaySmall',
  font1350: 'DisplayMedium',
  font1450: 'DisplayLarge'
};
const capitalize = (key: string): string => key.charAt(0).toUpperCase() + key.slice(1);

/**
 * Resolves a standard v2 ramp key to a `Font`-shaped style — raw values or
 * CSS-var references. `@uber/base-specs`'s `TypographyStyle.fontWeight` is
 * typed `number | string` (the CSS-var path's `fontWeight` is a
 * `var(--base-*)` reference), which is wider than `Font.fontWeight` — kept
 * narrow so `Font` stays DOM-`style`-compatible for its many other call
 * sites. The cast confines that widening to this one boundary.
 */
function resolveV2TypographyStyle(v2Key: TypographyRampKey, cssTheming: boolean): Font {
  return ({
    ...(cssTheming ? typographyRampCssVars[v2Key] : typographyRamp[v2Key])
  } as Font);
}

/**
 * Resolves a standard v2 ramp key to its mono counterpart. Mono ramp keys are
 * always `mono` + the capitalized standard key (e.g. `headlineXlBold` →
 * `monoHeadlineXlBold`) — see `MonoTypographyRampKey` in `@uber/base-specs`.
 */
function resolveV2MonoTypographyStyle(v2Key: TypographyRampKey, cssTheming: boolean): Font {
  const monoKey = (`mono${capitalize(v2Key)}` as MonoTypographyRampKey);
  return ({
    ...(cssTheming ? monoTypographyRampCssVars[monoKey] : monoTypographyRamp[monoKey])
  } as Font);
}

/**
 * Font-value equality check used to detect whether a customer has already
 * overridden a named typography style away from BaseUI's v1 default. Unlike
 * color tokens (primitive strings, comparable with `!==`), `Font` values are
 * objects, and object identity is *not* preserved across theme cloning or
 * `deepMerge` — both of which theme construction (`createLightTheme`/
 * `createDarkTheme`) routes every theme through. Value equality on the
 * visible fields is therefore the only reliable override signal.
 */
function fontsAreEqual(a: Font | undefined, b: Font | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.fontFamily === b.fontFamily && a.fontWeight === b.fontWeight && a.fontSize === b.fontSize && a.lineHeight === b.lineHeight && a.letterSpacing === b.letterSpacing;
}

/**
 * For each named typography style (standard + mono) with a v1 → v2 mapping,
 * **and** each raw `font*` scale entry (migrated via the named style it
 * backs — see {@link RAW_SCALE_TO_BASEUI_KEY}, since plenty of components
 * read the raw scale directly instead of the named alias), replaces it with
 * the corresponding Typography 2.0 style — resolved to either literal
 * values (JS path) or `var(--base-*)` references (CSS path) — unless the
 * caller has overridden that style away from the BaseUI v1 default
 * (detected by value inequality against the `v1Typography` baseline).
 * Mirrors `upgradeV1ColorsToV2`'s override-preserving contract.
 *
 * Unlike colors, every named typography style IS a migration target —
 * there's no primitive/semantic split — so there's no `renameCollidingV2Keys`
 * analog needed here. Exported for tests; internal.
 */
export function upgradeV1TypographyToV2(themeTypography: Typography, v1Typography: Typography, cssTheming: boolean): Partial<Typography> {
  const out: Record<string, Font> = {};
  for (const [baseuiKey, v1Key] of Object.entries(BASEUI_TO_V1_TYPOGRAPHY_KEY)) {
    const mapping = mapV1TypographyToV2(v1Key);
    if (!mapping) continue;
    const key = (baseuiKey as keyof Typography);
    if (!fontsAreEqual((themeTypography[key] as Font | undefined), (v1Typography[key] as Font | undefined))) {
      continue;
    }
    out[baseuiKey] = resolveV2TypographyStyle(mapping.v2, cssTheming);
  }
  for (const [baseuiKey, v1MonoKey] of Object.entries(BASEUI_TO_V1_MONO_TYPOGRAPHY_KEY)) {
    const mapping = mapV1MonoTypographyToV2(v1MonoKey);
    if (!mapping) continue;
    const key = (baseuiKey as keyof Typography);
    if (!fontsAreEqual((themeTypography[key] as Font | undefined), (v1Typography[key] as Font | undefined))) {
      continue;
    }
    out[baseuiKey] = resolveV2MonoTypographyStyle(mapping.v2, cssTheming);
  }
  for (const [rawKey, baseuiKey] of Object.entries(RAW_SCALE_TO_BASEUI_KEY)) {
    const v1Key = BASEUI_TO_V1_TYPOGRAPHY_KEY[baseuiKey];
    const mapping = v1Key ? mapV1TypographyToV2(v1Key) : undefined;
    if (!mapping) continue;
    const key = (rawKey as keyof Typography);
    if (!fontsAreEqual((themeTypography[key] as Font | undefined), (v1Typography[key] as Font | undefined))) {
      continue;
    }
    out[rawKey] = resolveV2TypographyStyle(mapping.v2, cssTheming);
  }
  return (out as Partial<Typography>);
}